import type { FC, ReactNode, RefObject } from 'react';
import S, { cm, kw, fn, pr, st, nm } from '../features/shared/SyntaxSpan';

/* Hero workspace preview – code lines for typing animation */
export const heroCodeHighlightLines = new Set([3, 9]);
export const heroCodeData: ReactNode[] = [
  <><S c={kw}>function</S> <S c={fn}>define</S>(<S c={pr}>ctx</S>) {'{'}</>,
  <>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;fastLength&apos;</S>, {'{'}</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>, <S c={pr}>default</S>: <S c={nm}>9</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>5</S>, <S c={pr}>max</S>: <S c={nm}>50</S>, <S c={pr}>step</S>: <S c={nm}>5</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</>,
  <>&nbsp;&nbsp;{'}'});</>,
  <S c={cm}>{' '}</S>,
  <>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>param</S>(<S c={st}>&apos;slowLength&apos;</S>, {'{'}</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>type</S>: <S c={st}>&apos;int&apos;</S>, <S c={pr}>default</S>: <S c={nm}>21</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>min</S>: <S c={nm}>10</S>, <S c={pr}>max</S>: <S c={nm}>200</S>, <S c={pr}>step</S>: <S c={nm}>10</S>,</>,
  <>&nbsp;&nbsp;&nbsp;&nbsp;<S c={pr}>optimize</S>: <S c={kw}>true</S>,</>,
  <>&nbsp;&nbsp;{'}'});</>,
  <>{'}'}</>,
  <S c={cm}>{' '}</S>,
  <S c={cm}>{'// Entry logic'}</S>,
  <><S c={kw}>if</S> (<S c={fn}>emaCrossUp</S>(<S c={pr}>ctx</S>)) {'{'}</>,
  <>&nbsp;&nbsp;<S c={pr}>ctx</S>.<S c={fn}>enterLong</S>()</>,
  <>{'}'}</>,
  <S c={cm}>{' '}</S>,
  <S c={cm}>{'// Exit logic'}</S>,
];

/* Hero workspace preview – result lines for terminal reveal */
export const heroResultData: ReactNode[] = [
  <>Backtest completed | 142 trades | 10,000 USDT</>,
  <span className="text-gray-600">{'────────────────────────────────────────'}</span>,
  <span className="text-gray-500">Performance</span>,
  <>{'  Total Return    '}<span className="text-green-400">+34.72%</span></>,
  <>{'  Max Drawdown    '}<span className="text-red-400">-12.40%</span></>,
  <>{'  Sharpe Ratio    '}<span className="text-green-400">0.82</span></>,
  <>{'  Win Rate        '}<span className="text-gray-300">43.20%</span></>,
  <span className="text-gray-500">Risk / Reward</span>,
  <>{'  Expectancy      '}<span className="text-green-400">+$24</span></>,
  <>{'  Payoff Ratio    '}<span className="text-green-400">2.14</span></>,
  <>{'  Avg Win         '}<span className="text-green-400">+$156</span></>,
  <>{'  Avg Loss        '}<span className="text-red-400">-$73</span></>,
  <span className="text-gray-600">{'────────────────────────────────────────'}</span>,
  <span className="text-cyan-400">View Full Analysis</span>,
];

interface Props {
  previewRef: RefObject<HTMLDivElement | null>;
  heroVisibleCode: number;
  heroVisibleResults: number;
  heroAfProgress: number;
}

const HeroWorkspacePreview: FC<Props> = ({ previewRef, heroVisibleCode, heroVisibleResults, heroAfProgress }) => (
  <>
    <style>{`@keyframes heroBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
    <div ref={previewRef} className="max-w-[1200px] mx-auto mt-16 relative z-10 overflow-hidden" style={{ maxHeight: '420px' }}>
      {/* Fade-out gradient at the bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0e14] to-transparent z-20 pointer-events-none" />

      <div className="rounded-t-lg border border-b-0 border-white/[0.08] overflow-hidden shadow-2xl shadow-black/60 font-mono text-[12px] leading-[1.6]">
        {/* Top bar */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06]" style={{ background: '#0c1520' }}>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-[7px] h-[7px] rounded-full bg-red-500/40" />
              <span className="w-[7px] h-[7px] rounded-full bg-yellow-500/40" />
              <span className="w-[7px] h-[7px] rounded-full bg-green-500/40" />
            </div>
            <span className="text-[10px] text-gray-500">Quanthop Research Workspace</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[9px] text-gray-600 select-none">
            <div className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70" />
              <span>Connected</span>
            </div>
            <span className="text-gray-700">|</span>
            <span>ema_cross v3</span>
            <span className="text-gray-700">|</span>
            <span>BTCUSDT &middot; 1D</span>
          </div>
        </div>

        <div className="flex" style={{ background: '#1e1e2e' }}>
          {/* Panel 1: Code editor */}
          <div className="flex-1 min-w-0 border-r border-white/[0.06]">
            <div className="flex items-center border-b border-white/[0.06]" style={{ background: '#181825' }}>
              <div className="flex items-center gap-2 px-3 py-1 border-b-2 border-b-blue-400/60" style={{ background: '#1e1e2e' }}>
                <svg className="w-3 h-3 text-blue-400/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <span className="text-[10px] text-gray-300">strategy.qsl</span>
              </div>
            </div>

            <div className="flex py-2.5">
              <div className="flex flex-col pr-3 pl-3 select-none flex-shrink-0 text-[11px] leading-[1.6] text-right" style={{ color: 'rgba(133,133,133,0.35)' }}>
                {heroCodeData.map((_, i) => (
                  <div key={i} style={{ opacity: i < heroVisibleCode ? 1 : 0 }}>{i + 1}</div>
                ))}
              </div>
              <div className="text-[11px] leading-[1.6] text-gray-300/90 pr-4 whitespace-pre">
                {heroCodeData.map((line, i) => (
                  <div
                    key={i}
                    style={{
                      opacity: i < heroVisibleCode ? 1 : 0,
                      ...(heroCodeHighlightLines.has(i) && i < heroVisibleCode ? { background: 'rgba(111,179,242,0.06)' } : {}),
                    }}
                  >
                    {line}
                    {i === heroVisibleCode - 1 && heroVisibleCode < heroCodeData.length && (
                      <span style={{ color: '#aeafad', animation: 'heroBlink 1s step-end infinite' }}>|</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Panel 2: Results terminal */}
          <div className="hidden md:flex flex-col w-[320px] flex-shrink-0 border-r border-white/[0.06]" style={{ background: '#0f1923' }}>
            <div className="flex items-center gap-3 px-3 py-1 border-b border-white/[0.06]" style={{ background: '#0c1520' }}>
              <span className="text-[10px] text-cyan-400/80">Results</span>
              <span className="text-[10px] text-gray-600">Trades</span>
              <span className="text-[10px] text-gray-600">Chart</span>
            </div>

            <pre className="px-3 py-2.5 text-[10px] leading-snug text-gray-300 whitespace-pre flex-1">
              {heroResultData.map((line, i) => (
                <span key={i} style={{ display: 'block', opacity: i < heroVisibleResults ? 1 : 0, transition: 'opacity 0.15s ease' }}>{line}</span>
              ))}
            </pre>
          </div>

          {/* Panel 3: Adaptive Flow */}
          <div className="hidden lg:flex flex-col w-[340px] flex-shrink-0" style={{ background: '#0f1923' }}>
            <div className="flex items-center justify-between px-3 py-1 border-b border-white/[0.06]" style={{ background: '#0c1520' }}>
              <span className="text-[10px] text-emerald-400/80">Adaptive Flow</span>
              <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded" style={{ background: 'rgba(34,197,94,0.1)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="text-[9px] text-emerald-400">Running</span>
              </div>
            </div>

            {/* Health assessment */}
            <div className="px-3 py-2.5 border-b border-white/[0.04]" style={{ opacity: heroAfProgress > 0 ? 1 : 0.05, transition: 'opacity 0.4s ease' }}>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <span className="text-[20px] font-bold text-emerald-400">{(heroAfProgress * 7.4).toFixed(1)}</span>
                  <p className="text-[8px] text-gray-500 uppercase tracking-wider">Health</p>
                </div>
                <div className="h-8 w-px bg-gray-700/50" />
                <div className="text-center">
                  <span className="text-[20px] font-bold text-white">{Math.round(heroAfProgress * 68)}<span className="text-[12px] text-gray-500">%</span></span>
                  <p className="text-[8px] text-gray-500 uppercase tracking-wider">Confidence</p>
                </div>
                <div className="h-8 w-px bg-gray-700/50" />
                <div className="text-center">
                  <span className="text-[20px] font-bold text-white">{Math.round(heroAfProgress * 100)}</span>
                  <p className="text-[8px] text-gray-500 uppercase tracking-wider">MC Robust</p>
                </div>
              </div>
            </div>

            {/* Expected vs Actual */}
            <div className="px-3 py-2.5 border-b border-white/[0.04]" style={{ opacity: heroAfProgress > 0.3 ? 1 : 0, transition: 'opacity 0.3s ease' }}>
              <span className="text-[9px] text-gray-500 uppercase tracking-wider font-medium">Expected vs Actual</span>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { label: 'Win Rate', expected: '40.0%', actual: '41.7%' },
                  { label: 'Sharpe', expected: '1.00', actual: '0.94' },
                  { label: 'Max DD', expected: '21.9%', actual: '18.3%' },
                ].map(m => (
                  <div key={m.label} className="text-center">
                    <p className="text-[8px] text-gray-600 uppercase tracking-wider">{m.label}</p>
                    <p className="text-[11px] text-white font-medium">{m.expected}</p>
                    <p className="text-[8px] text-gray-500">Actual: <span className="text-emerald-400/80">{m.actual}</span></p>
                  </div>
                ))}
              </div>
            </div>

            {/* Re-opt progress */}
            <div className="px-3 py-2.5 border-b border-white/[0.04]" style={{ opacity: heroAfProgress > 0.3 ? 1 : 0, transition: 'opacity 0.3s ease' }}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[9px] text-gray-500 uppercase tracking-wider">Re-optimization</span>
                <span className="text-[9px] text-gray-400">{Math.round(heroAfProgress * 247)} / 720</span>
              </div>
              <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full bg-emerald-400/70" style={{ width: `${heroAfProgress * 34}%`, transition: 'width 0.1s linear' }} />
              </div>
            </div>

            {/* Active parameters */}
            <div className="px-3 py-2.5" style={{ opacity: heroAfProgress > 0.5 ? 1 : 0, transition: 'opacity 0.3s ease' }}>
              <span className="text-[9px] text-gray-500 uppercase tracking-wider">Active Parameters</span>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[10px] bg-white/[0.06] text-gray-300 px-2 py-0.5 rounded">fast: <span className="text-white font-medium">34</span></span>
                <span className="text-[10px] bg-white/[0.06] text-gray-300 px-2 py-0.5 rounded">slow: <span className="text-white font-medium">40</span></span>
              </div>
              <p className="text-[8px] text-gray-600 mt-1.5">12 live trades &middot; Last optimized Jan 8</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
);

export default HeroWorkspacePreview;
